import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../LanguageContext';
import { useAuth } from '../AuthContext';
import {
  getAssistantStatus, askAssistant,
  getAssistantConfig, saveAssistantKey, clearAssistantKey,
  listAssistantConversations, getAssistantConversation, deleteAssistantConversation,
} from '../api';

// ── Lightweight assistant-message renderer (no markdown dependency) ──
// The model often replies with markdown tables; rendered as raw pre-wrap text those
// were unreadable. Turn pipe-tables into real, scrollable tables and give **bold** /
// `code` basic styling — everything else stays as plain wrapped text.
function renderInline(str) {
  const nodes = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0, m, key = 0;
  while ((m = re.exec(str)) !== null) {
    if (m.index > last) nodes.push(str.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith('**')) nodes.push(<strong key={key++}>{tok.slice(2, -2)}</strong>);
    else nodes.push(<code key={key++} style={{ background: 'var(--bg-primary)', padding: '1px 5px', borderRadius: 5, fontSize: '0.85em' }}>{tok.slice(1, -1)}</code>);
    last = m.index + tok.length;
  }
  if (last < str.length) nodes.push(str.slice(last));
  return nodes;
}

const isTableSep = (l) => l != null && l.includes('-') && /^\s*\|?[\s:|-]+\|?\s*$/.test(l);
const splitRow = (l) => l.replace(/^\s*\|/, '').replace(/\|\s*$/, '').split('|').map(c => c.trim());

function MdTable({ lines, isRTL }) {
  const header = splitRow(lines[0]);
  const rows = lines.slice(2).map(splitRow);
  const cell = { border: '1px solid var(--border)', padding: '6px 10px', fontSize: '0.85rem', textAlign: isRTL ? 'right' : 'left', whiteSpace: 'nowrap' };
  return (
    <div style={{ overflowX: 'auto', margin: '6px 0', maxWidth: '100%' }}>
      <table style={{ borderCollapse: 'collapse' }}>
        <thead><tr>{header.map((h, i) => <th key={i} style={{ ...cell, background: 'var(--bg-primary)', fontWeight: 700 }}>{renderInline(h)}</th>)}</tr></thead>
        <tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((c, ci) => <td key={ci} style={cell}>{renderInline(c)}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

function AssistantMd({ text, isRTL }) {
  const lines = String(text || '').split('\n');
  const isStart = (idx) => lines[idx].includes('|') && isTableSep(lines[idx + 1]);
  const out = [];
  let i = 0, key = 0;
  while (i < lines.length) {
    if (isStart(i)) {
      const tbl = [lines[i], lines[i + 1]];
      let j = i + 2;
      while (j < lines.length && lines[j].includes('|')) { tbl.push(lines[j]); j++; }
      out.push(<MdTable key={key++} lines={tbl} isRTL={isRTL} />);
      i = j;
    } else {
      const txt = [];
      while (i < lines.length && !isStart(i)) { txt.push(lines[i]); i++; }
      const joined = txt.join('\n').replace(/^\n+|\n+$/g, '');
      if (joined) out.push(<div key={key++} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{renderInline(joined)}</div>);
    }
  }
  return <>{out}</>;
}

function ConfigPanel({ t, cfg, onChange }) {
  const [key, setKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const save = async () => {
    const k = key.trim();
    if (!k.startsWith('sk-ant-') || k.length < 20) { setErr(t('assistantKeyInvalid')); return; }
    setSaving(true); setErr('');
    const res = await saveAssistantKey(k);
    setSaving(false);
    if (res.error) { setErr(res.error === 'invalid_key' ? t('assistantKeyInvalid') : t('assistantError')); return; }
    setKey('');
    onChange({ configured: true, source: 'db', editable: true, masked: res.masked });
  };

  const remove = async () => {
    const res = await clearAssistantKey();
    if (!res.error) onChange({ configured: false, source: null, editable: true });
  };

  return (
    <div style={{ maxWidth: 520, margin: '24px auto', padding: 20, borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
      <div style={{ fontWeight: 800, fontSize: '1rem', color: 'var(--text-primary)', marginBottom: 6 }}>{t('assistantEnable')}</div>
      <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 14 }}>{t('assistantKeyHint')}</div>
      {cfg.configured && cfg.masked && (
        <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginBottom: 10 }}><code>{cfg.masked}</code></div>
      )}
      <input
        type="password" value={key} onChange={(e) => setKey(e.target.value)}
        placeholder={t('assistantKeyPlaceholder')} autoComplete="off" dir="ltr"
        style={{ width: '100%', padding: '11px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-primary)', color: 'var(--text-primary)', fontSize: '0.9rem', fontFamily: 'monospace' }}
      />
      {err && <div style={{ color: 'var(--danger, #dc2626)', fontSize: '0.8rem', marginTop: 8 }}>{err}</div>}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 14, flexWrap: 'wrap' }}>
        <button onClick={save} disabled={saving || !key.trim()}
          style={{ padding: '10px 18px', borderRadius: 10, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: '0.88rem', cursor: (saving || !key.trim()) ? 'not-allowed' : 'pointer', opacity: (saving || !key.trim()) ? 0.5 : 1 }}>
          {t('assistantSaveKey')}
        </button>
        {cfg.configured && cfg.source === 'db' && (
          <button onClick={remove} style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'none', color: 'var(--text-secondary)', fontSize: '0.82rem', cursor: 'pointer' }}>
            {t('assistantRemoveKey')}
          </button>
        )}
        <a href="https://console.anthropic.com" target="_blank" rel="noreferrer" style={{ fontSize: '0.78rem', color: 'var(--accent)', textDecoration: 'none' }}>
          {t('assistantGetKey')}
        </a>
      </div>
    </div>
  );
}

export default function Assistant() {
  const { t, lang, isRTL } = useLanguage();
  const { user } = useAuth();
  const isManager = user?.role === 'admin' || user?.role === 'manager';
  const [messages, setMessages] = useState([]);        // { role, content }
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [showManage, setShowManage] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [convId, setConvId] = useState(null);
  const [showHistory, setShowHistory] = useState(true);
  const scrollRef = useRef(null);

  const refreshConversations = useCallback(async () => {
    const res = await listAssistantConversations();
    if (!res.error) setConversations(res.conversations || []);
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      let c;
      if (isManager) {
        c = await getAssistantConfig();
        if (!(c && !c.error)) c = null;
      }
      if (!c) {
        const s = await getAssistantStatus();
        c = { configured: s.configured !== false, editable: false };
      }
      if (!alive) return;
      setCfg(c);
      if (c.configured) refreshConversations();
    })();
    return () => { alive = false; };
  }, [isManager, refreshConversations]);

  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  const suggestions = lang === 'ar'
    ? [
        'ما أهم أسباب رفض العملاء هذا الشهر؟',
        'كيف يمكن لكل موظف مبيعات تحسين أدائه؟',
        'أي مصدر للعملاء المحتملين يحقّق أعلى نسبة تحويل؟',
      ]
    : [
        'What are the top reject reasons this month?',
        'How can each sales member improve their performance?',
        'Which lead source converts best?',
      ];

  const configured = !!cfg?.configured;

  const newChat = () => { setMessages([]); setConvId(null); setInput(''); };

  const selectConversation = async (id) => {
    if (loading) return;
    const res = await getAssistantConversation(id);
    if (res.error) return;
    setConvId(res.id);
    setMessages((res.messages || []).map((m) => ({ role: m.role, content: m.content })));
  };

  const removeConversation = async (id, e) => {
    e.stopPropagation();
    const res = await deleteAssistantConversation(id);
    if (!res.error) {
      setConversations((cs) => cs.filter((c) => c.id !== id));
      if (id === convId) newChat();
    }
  };

  const send = useCallback(async (text) => {
    const q = (text != null ? text : input).trim();
    if (!q || loading || !configured) return;
    const history = [...messages, { role: 'user', content: q }];
    setMessages(history);
    setInput('');
    setLoading(true);
    const res = await askAssistant(history, convId);
    setLoading(false);
    if (res.error) {
      const msg = res.status === 503 ? t('assistantNotConfigured') : t('assistantError');
      setMessages((m) => [...m, { role: 'assistant', content: msg, error: true }]);
      return;
    }
    setMessages((m) => [...m, { role: 'assistant', content: res.answer || '', queries: res.queries, usage: res.usage }]);
    if (res.conversationId) setConvId(res.conversationId);
    refreshConversations();
  }, [input, loading, configured, messages, convId, t, refreshConversations]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const showConfigPanel = cfg && ((!configured && isManager && cfg.editable !== false) || showManage);

  if (showConfigPanel) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 900, margin: '0 auto' }}>
        <ConfigPanel t={t} cfg={cfg} onChange={(c) => { setCfg(c); setShowManage(false); if (c.configured) refreshConversations(); }} />
      </div>
    );
  }

  if (cfg && !configured) {
    return (
      <div dir={isRTL ? 'rtl' : 'ltr'} style={{ maxWidth: 700, margin: '24px auto', padding: '12px 16px', borderRadius: 10, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.4)', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
        {isManager && cfg.source === 'env' ? t('assistantKeyEnvNote') : t('assistantContactAdmin')}
      </div>
    );
  }

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} style={{ display: 'flex', height: 'calc(100vh - 130px)', maxWidth: 1100, margin: '0 auto', gap: 0 }}>
      {showHistory && (
        <aside style={{ width: 240, flexShrink: 0, display: 'flex', flexDirection: 'column', borderInlineEnd: '1px solid var(--border)', paddingInlineEnd: 10 }}>
          <button onClick={newChat} style={{ margin: '2px 0 10px', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer' }}>
            + {t('assistantClear')}
          </button>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {conversations.length === 0 && (
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.8rem', padding: '8px 4px' }}>{t('assistantNoHistory')}</div>
            )}
            {conversations.map((c) => (
              <div key={c.id} onClick={() => selectConversation(c.id)}
                style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 10px', marginBottom: 4, borderRadius: 8, cursor: 'pointer', fontSize: '0.83rem', background: c.id === convId ? 'var(--bg-secondary)' : 'transparent', border: c.id === convId ? '1px solid var(--border)' : '1px solid transparent' }}>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: 'var(--text-primary)' }}>{c.title || '—'}</span>
                <button onClick={(e) => removeConversation(c.id, e)} title={t('assistantDeleteChat')}
                  style={{ background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '1rem', lineHeight: 1, padding: '0 2px' }}>&times;</button>
              </div>
            ))}
          </div>
        </aside>
      )}

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', paddingInlineStart: showHistory ? 12 : 0, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, paddingBottom: 6 }}>
          <button onClick={() => setShowHistory((v) => !v)} title={t('assistantHistory')}
            style={{ background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px', color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '0.8rem' }}>
            &#9776; {t('assistantHistory')}
          </button>
        </div>

        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '4px 2px' }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--text-secondary)', marginTop: 40 }}>
              <div style={{ fontSize: '2.4rem', marginBottom: 12 }}>&#128172;</div>
              <div style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: 8 }}>{t('assistantTitle')}</div>
              <div style={{ maxWidth: 520, margin: '0 auto 20px', lineHeight: 1.7 }}>{t('assistantIntro')}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 520, margin: '0 auto' }}>
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => send(s)} disabled={!configured || loading}
                    style={{ textAlign: isRTL ? 'right' : 'left', padding: '10px 14px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', cursor: (!configured || loading) ? 'not-allowed' : 'pointer', opacity: (!configured || loading) ? 0.6 : 1, fontSize: '0.9rem' }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {messages.map((m, i) => (
            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: m.role === 'user' ? 'flex-end' : 'flex-start', marginBottom: 12 }}>
              <div style={{ maxWidth: '82%', padding: '10px 14px', borderRadius: 14, whiteSpace: (m.role === 'assistant' && !m.error) ? 'normal' : 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.7, fontSize: '0.92rem', background: m.role === 'user' ? 'var(--accent)' : 'var(--bg-secondary)', color: m.role === 'user' ? '#fff' : (m.error ? 'var(--danger, #dc2626)' : 'var(--text-primary)'), border: m.role === 'user' ? 'none' : '1px solid var(--border)' }}>
                {(m.role === 'assistant' && !m.error) ? <AssistantMd text={m.content} isRTL={isRTL} /> : m.content}
              </div>
              {m.role === 'assistant' && m.queries && m.queries.length > 0 && (
                <details style={{ maxWidth: '82%', marginTop: 4, fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                  <summary style={{ cursor: 'pointer' }}>
                    {t('assistantSqlUsed')}
                    {m.usage ? ` · ${(m.usage.inputTokens + m.usage.outputTokens).toLocaleString()} tokens ≈ $${(m.usage.inputTokens * 5 / 1e6 + m.usage.outputTokens * 25 / 1e6).toFixed(3)}` : ''}
                  </summary>
                  <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', marginTop: 6, direction: 'ltr', textAlign: 'left', overflowX: 'auto' }}>{m.queries.join('\n\n')}</pre>
                </details>
              )}
            </div>
          ))}

          {loading && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
              <div style={{ padding: '10px 14px', borderRadius: 14, background: 'var(--bg-secondary)', border: '1px solid var(--border)', color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                {t('assistantThinking')}
              </div>
            </div>
          )}
        </div>

        <div style={{ marginTop: 10 }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
            <textarea value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={onKeyDown}
              placeholder={t('assistantPlaceholder')} disabled={!configured} rows={1}
              style={{ flex: 1, resize: 'none', maxHeight: 140, padding: '12px 14px', borderRadius: 12, border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)', fontSize: '0.92rem', fontFamily: 'inherit', lineHeight: 1.5 }} />
            <button onClick={() => send()} disabled={!configured || loading || !input.trim()}
              style={{ padding: '12px 20px', borderRadius: 12, border: 'none', background: 'var(--accent)', color: '#fff', fontWeight: 700, fontSize: '0.9rem', cursor: (!configured || loading || !input.trim()) ? 'not-allowed' : 'pointer', opacity: (!configured || loading || !input.trim()) ? 0.5 : 1, whiteSpace: 'nowrap' }}>
              {t('assistantSend')}
            </button>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, gap: 12 }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{t('assistantDisclaimer')}</span>
            {isManager && configured && cfg?.editable && (
              <button onClick={() => setShowManage(true)} style={{ fontSize: '0.75rem', background: 'none', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 10px', color: 'var(--text-secondary)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {t('assistantManageKey')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
